import { Link } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import { formatTime } from "@/utils/format";
import type { Match } from "@/types";

type MatchRowProps = {
  match: Match;
};

export default function MatchRow({ match }: MatchRowProps) {
  const isLive = match.status === "live";
  const isFinished = match.status === "finished";
  return (
    <Link
      to={`/match/${match.id}`}
      className="group block bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 md:p-5">
        <div className="flex items-center sm:flex-col sm:items-start gap-2 sm:gap-1 shrink-0 sm:w-24">
          <span className="font-label font-semibold text-lg text-foreground-900">
            {formatTime(match.startTime)}
          </span>
          <span className="text-xs text-foreground-500">{match.leagueName}</span>
        </div>

        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 min-w-0 sm:justify-end">
            <span className="text-sm font-medium text-foreground-900 truncate order-2 sm:order-1 sm:text-right">
              {match.home.name}
            </span>
            <span className="order-1 sm:order-2">
              <TeamBadge shortName={match.home.shortName} color={match.home.color} size="sm" />
            </span>
          </div>

          <div className="flex items-center justify-center gap-2 min-w-[84px]">
            {isFinished || isLive ? (
              <span className="font-heading font-semibold text-xl text-foreground-950">
                <span className={isLive ? "text-accent-600" : ""}>{match.homeScore}</span>
                <span className="mx-1.5 text-foreground-400">:</span>
                <span className={isLive ? "text-accent-600" : ""}>{match.awayScore}</span>
              </span>
            ) : (
              <span className="font-label text-sm text-foreground-500">VS</span>
            )}
          </div>

          <div className="flex items-center gap-3 min-w-0">
            <span>
              <TeamBadge shortName={match.away.shortName} color={match.away.color} size="sm" />
            </span>
            <span className="text-sm font-medium text-foreground-900 truncate">
              {match.away.name}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 sm:w-40">
          <StatusBadge status={match.status} />
          <span className="text-xs text-foreground-500 hidden lg:block truncate max-w-[88px]">
            {match.stage}
          </span>
          <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-600 transition-colors" />
        </div>
      </div>
    </Link>
  );
}